import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Icon from './Icon';

/**
 * Hear what a muezzin or a reciter sounds like before choosing them.
 *
 * This replaces react-h5-audio-player, which was rendering with no styling at
 * all: its stylesheet was never imported, so every part of it fell back to a
 * block element and stacked -- two times on their own lines, a play button and a
 * volume button in little grey squares underneath. It looked broken because it
 * was.
 *
 * Importing that stylesheet would have fixed the stacking and brought a second
 * visual language into a sheet that already has one. What is actually needed
 * here is a play button, a line showing where you are, and the time -- so that
 * is what this is, in the app's own colours, and the dependency goes.
 *
 * No volume control. The box plays through the television and its volume is the
 * television's; a slider here would look like it did something.
 */

const mmss = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

const AudioPreview = ({ src, label }) => {
  const el = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [at, setAt] = useState(0);
  const [length, setLength] = useState(0);

  // Choosing a different reciter starts again from silence. Playing on its own
  // would fill the room from a settings screen, which is nobody's intention.
  useEffect(() => {
    setPlaying(false);
    setAt(0);
    setLength(0);
    if (el.current) el.current.pause();
  }, [src]);

  const toggle = () => {
    const audio = el.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().catch(() => setPlaying(false));
    } else {
      audio.pause();
    }
  };

  const seek = (event) => {
    const audio = el.current;
    if (!audio || !length) return;
    const box = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(Math.max((event.clientX - box.left) / box.width, 0), 1);
    audio.currentTime = ratio * length;
    setAt(audio.currentTime);
  };

  const done = length ? (at / length) * 100 : 0;

  return (
    <div className="preview">
      <button
        type="button"
        className={'preview__play' + (playing ? ' preview__play--on' : '')}
        onClick={toggle}
        aria-label={playing ? 'Pause the sample' : (label || 'Play a sample')}
      >
        <Icon name={playing ? 'stop' : 'play'} size={14} />
      </button>

      {/* The hit area is the padded wrapper, not the six-pixel line inside it:
          this is used with a thumb on a phone. */}
      <div
        className="preview__seek"
        onClick={seek}
        role="slider"
        tabIndex={0}
        aria-label="Position"
        aria-valuemin={0}
        aria-valuemax={Math.round(length)}
        aria-valuenow={Math.round(at)}
        onKeyDown={(e) => {
          const audio = el.current;
          if (!audio || !length) return;
          if (e.key === 'ArrowRight') audio.currentTime = Math.min(at + 5, length);
          if (e.key === 'ArrowLeft') audio.currentTime = Math.max(at - 5, 0);
        }}
      >
        <div className="preview__track">
          <span className="preview__fill" style={{ width: `${done}%` }} />
        </div>
      </div>

      <span className="preview__time">{mmss(at)} / {mmss(length)}</span>

      <audio
        ref={el}
        src={src}
        preload="metadata"
        onLoadedMetadata={(e) => setLength(e.currentTarget.duration)}
        onDurationChange={(e) => setLength(e.currentTarget.duration)}
        onTimeUpdate={(e) => setAt(e.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setAt(0); }}
      />
    </div>
  );
};

AudioPreview.propTypes = {
  src:   PropTypes.string,
  label: PropTypes.string,
};

export default AudioPreview;
